import { ReactNode } from 'react';
import clsx from 'clsx';
import { ArrowRightIcon } from '../../../assets/icons';
import { formatPercentages, formatUSD } from '../../../helpers';
import Card from '../../../components/Card';
import { parseUnits } from 'ethers/lib/utils';
import AssetType from '../../../components/AssetType';
import { config } from '../../../config/config';
import { TradePair } from '../../../context/chart/chartTypes';

type PreviewDataType<T> = {
    value: T;
    previewValue?: T;
};

type ClosePositionPreviewProps = {
    positionDetails: {
        tradePair?: TradePair;
        isShort: boolean;
        size: PreviewDataType<string>;
        collateral: PreviewDataType<string>;
        bankruptcyPrice: PreviewDataType<string>;
        leverage: PreviewDataType<string>;
        markPrice: PreviewDataType<string>;
        entryPrice: PreviewDataType<string>;
        pnl: PreviewDataType<string | undefined>;
        fees: PreviewDataType<string | undefined>;
    };
};

type EntryProps = {
    title: ReactNode;
    oldValue: string | undefined;
    newValue: string | undefined;
    className: string;
    oldValueClassName?: string;
    newValueClassName?: string;
};

const Entry: React.FC<EntryProps> = ({
    title,
    oldValue,
    className,
    newValue,
    oldValueClassName,
    newValueClassName,
}) => (
    <div className={`entry entry__${className}`}>
        <div>
            <span className='title'>{title}:</span>
            <span className={`old-value ${oldValueClassName}`}>{oldValue}</span>
        </div>
        <span className={`new-value ${newValueClassName}`}>
            {newValue && (
                <>
                    <ArrowRightIcon />
                    {newValue}
                </>
            )}
        </span>
    </div>
);

const ClosePositionPreview: React.FC<ClosePositionPreviewProps> = ({
    positionDetails,
}) => {
    return (
        <Card variant='secondary' className='position-details'>
            <div className='position-type'>
                <AssetType
                    tradePair={positionDetails.tradePair}
                    isShort={positionDetails.isShort}
                    leverage={positionDetails.leverage.value}
                />
            </div>
            <div className='position-details__inner'>
                <Entry
                    className='size'
                    title='Size'
                    oldValue={
                        positionDetails.size.value
                            ? formatUSD(positionDetails.size.value, { minDecimals: 2 })
                            : '-'
                    }
                    newValue={
                        positionDetails.size.previewValue
                            ? formatUSD(positionDetails.size.previewValue, {
                                  minDecimals: 2,
                              })
                            : undefined
                    }
                />
                <Entry
                    className='collateral'
                    title='Collateral'
                    oldValue={
                        positionDetails.collateral.value
                            ? formatUSD(positionDetails.collateral.value, {
                                  minDecimals: 2,
                              })
                            : '-'
                    }
                    newValue={
                        positionDetails.collateral.previewValue
                            ? formatUSD(positionDetails.collateral.previewValue, {
                                  minDecimals: 2,
                              })
                            : undefined
                    }
                />
                <Entry
                    className='leverage'
                    title='Leverage'
                    oldValue={`${formatPercentages(positionDetails.leverage.value)}x`}
                    newValue={
                        positionDetails.leverage.previewValue
                            ? `${formatPercentages(
                                  positionDetails.leverage.previewValue
                              )}x`
                            : undefined
                    }
                />
                <Entry
                    className='entry-price'
                    title='Entry Price'
                    oldValue={
                        positionDetails.entryPrice.value
                            ? formatUSD(positionDetails.entryPrice.value, {
                                  minDecimals: 2,
                                  maxDecimals:
                                      positionDetails.tradePair?.decimalFormatting,
                              })
                            : '-'
                    }
                    newValue={
                        positionDetails.entryPrice.previewValue
                            ? formatUSD(positionDetails.entryPrice.previewValue, {
                                  minDecimals: 2,
                                  maxDecimals:
                                      positionDetails.tradePair?.decimalFormatting,
                              })
                            : undefined
                    }
                />
                <Entry
                    className='mark-price'
                    title='Mark Price'
                    oldValue={
                        positionDetails.markPrice.value
                            ? formatUSD(positionDetails.markPrice.value, {
                                  minDecimals: 2,
                                  maxDecimals:
                                      positionDetails.tradePair?.decimalFormatting,
                              })
                            : '-'
                    }
                    newValue={
                        positionDetails.markPrice.previewValue
                            ? formatUSD(positionDetails.markPrice.previewValue, {
                                  minDecimals: 2,
                                  maxDecimals:
                                      positionDetails.tradePair?.decimalFormatting,
                              })
                            : undefined
                    }
                />
                <Entry
                    className='liquidation-price'
                    title='Liq. Price'
                    oldValue={
                        positionDetails.bankruptcyPrice.value
                            ? formatUSD(positionDetails.bankruptcyPrice.value, {
                                  minDecimals: 2,
                                  maxDecimals:
                                      positionDetails.tradePair?.decimalFormatting,
                              })
                            : '-'
                    }
                    newValue={
                        positionDetails.bankruptcyPrice.previewValue
                            ? formatUSD(positionDetails.bankruptcyPrice.previewValue, {
                                  minDecimals: 2,
                                  maxDecimals:
                                      positionDetails.tradePair?.decimalFormatting,
                              })
                            : undefined
                    }
                />
                <Entry
                    className='pnl'
                    title='PnL'
                    oldValue={
                        positionDetails.pnl.value
                            ? formatUSD(positionDetails.pnl.value, { minDecimals: 2 })
                            : '-'
                    }
                    oldValueClassName={clsx({
                        increased:
                            positionDetails.pnl.value &&
                            parseUnits(
                                positionDetails.pnl.value,
                                config.priceFeedDecimal
                            ).gt('0'),
                        decreased:
                            positionDetails.pnl.value &&
                            parseUnits(
                                positionDetails.pnl.value,
                                config.priceFeedDecimal
                            ).lt('0'),
                    })}
                    newValue={
                        positionDetails.pnl.previewValue
                            ? formatUSD(positionDetails.pnl.previewValue, {
                                  minDecimals: 2,
                              })
                            : undefined
                    }
                    newValueClassName={clsx({
                        increased:
                            positionDetails.pnl.previewValue &&
                            parseUnits(
                                positionDetails.pnl.previewValue,
                                config.priceFeedDecimal
                            ).gt('0'),
                        decreased:
                            positionDetails.pnl.previewValue &&
                            parseUnits(
                                positionDetails.pnl.previewValue,
                                config.priceFeedDecimal
                            ).lt('0'),
                    })}
                />
                <Entry
                    className='fees'
                    title='Fees'
                    oldValue={
                        positionDetails.fees.value
                            ? formatUSD(positionDetails.fees.value, { minDecimals: 2 })
                            : formatUSD(0, { minDecimals: 2 })
                    }
                    newValue={
                        positionDetails.fees.previewValue
                            ? formatUSD(positionDetails.fees.previewValue, {
                                  minDecimals: 2,
                              })
                            : undefined
                    }
                />
            </div>
        </Card>
    );
};

export default ClosePositionPreview;
